import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HeartHandshake, ShieldCheck, Sparkles, Mail } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1758216434916-0819f9cd3a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdhcm0lMjBvcmdhbmljJTIwc2hhcGVzJTIwYmVpZ2UlMjBjYWxtfGVufDB8fHx8MTc4Mjg2Njg5N3ww&ixlib=rb-4.1.0&q=85";

const Login = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ provider: "emergent" });

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/auth/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (config.provider === "google" && config.google_client_id) {
      const redirectUrl = window.location.origin + "/auth/google";
      const params = new URLSearchParams({
        client_id: config.google_client_id,
        redirect_uri: redirectUrl,
        response_type: "code",
        scope: "openid email profile",
        access_type: "offline",
        prompt: "consent",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
      return;
    }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left visual panel */}
      <div className="relative lg:w-1/2 min-h-[240px] lg:min-h-screen overflow-hidden">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D2C28]/60 via-[#2D2C28]/10 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center">
              <HeartHandshake className="w-6 h-6 text-[#C07761]" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-bold text-xl text-white tracking-tight">
              SéanceSuivi
            </span>
          </div>
          <div className="hidden lg:block max-w-md">
            <h2 className="font-heading text-3xl font-semibold text-white leading-snug">
              Des retours personnalisés à vos patients, sans effort.
            </h2>
            <p className="text-white/80 mt-3 leading-relaxed">
              Importez la transcription de vos séances Plaud et laissez l'IA rédiger un e-mail
              de suivi juste, humain et confidentiel.
            </p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-[#FBF9F6]">
        <div className="w-full max-w-sm fade-up">
          <p className="text-sm font-medium text-[#C07761] tracking-wide uppercase">
            Espace thérapeute
          </p>
          <h1 className="font-heading text-4xl font-semibold text-[#2D2C28] tracking-tight mt-2">
            Bienvenue dans votre espace confidentiel.
          </h1>
          <p className="text-[#757169] mt-3 leading-relaxed">
            Connectez-vous pour gérer vos patients et générer vos e-mails de suivi.
          </p>

          <div className="mt-8 space-y-3">
            {[
              { icon: Sparkles, text: "E-mails générés par IA (Claude & GPT)" },
              { icon: Mail, text: "Envoi via votre propre serveur SMTP" },
              { icon: ShieldCheck, text: "Vos données restent privées" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-[#575349]">
                <div className="w-8 h-8 rounded-lg bg-[#EAE6DF] flex items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4 text-[#7B968B]" strokeWidth={1.5} />
                </div>
                <span className="text-sm">{f.text}</span>
              </div>
            ))}
          </div>

          <Button
            onClick={handleLogin}
            data-testid="google-login-btn"
            className="w-full mt-8 h-12 rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white text-base font-medium transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#fff" d="M12 11v3.6h5.1c-.2 1.3-1.6 3.9-5.1 3.9-3.1 0-5.6-2.5-5.6-5.6S8.9 7.3 12 7.3c1.7 0 2.9.7 3.6 1.4l2.5-2.4C16.5 4.8 14.5 4 12 4 6.9 4 2.8 8.1 2.8 13S6.9 22 12 22c6.1 0 8.4-4.3 8.4-8.6 0-.6-.1-1-.1-1.4H12z" />
            </svg>
            Continuer avec Google
          </Button>

          <p className="text-xs text-[#9c988f] mt-6 text-center leading-relaxed">
            En vous connectant, vous acceptez de traiter les données de vos patients
            dans le respect du secret professionnel.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
